import { RefObject, useEffect, useState } from "react"

type UseIntersectionObserverOptions = {
  /** The element that is used as the viewport for checking visibility of the target. Defaults to browser viewport if null. */
  root?: Element | null
  /** Margin around the root. Can have values similar to CSS margin property (e.g. "10px 20px 30px 40px"). Defaults to "0px". */
  rootMargin?: string
  /** Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed. Defaults to 0. */
  threshold?: number | number[]
}

/**
 * A React hook that tracks whether an element is intersecting with the viewport or a parent element.
 * Uses the Intersection Observer API to efficiently detect when an element enters the visible area.
 *
 * Once the element becomes visible, the hook returns `true` and stays `true` (does not reset on exit).
 * This is useful for lazy loading content that should load once when scrolled into view.
 *
 * @param ref - A React ref object pointing to the DOM element to observe
 * @param options - Configuration options for the Intersection Observer
 * @returns `true` when the element has intersected (been visible), `false` otherwise
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const ref = useRef<HTMLDivElement>(null)
 *   const isVisible = useIntersectionObserver(ref, {
 *     rootMargin: "100px", // Start loading 100px before element enters viewport
 *   })
 *
 *   return (
 *     <div ref={ref}>
 *       {isVisible ? <ExpensiveComponent /> : <Skeleton />}
 *     </div>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Lazy load images
 * const LazyImage = ({ src, alt }) => {
 *   const imgRef = useRef<HTMLImageElement>(null)
 *   const isVisible = useIntersectionObserver(imgRef, { rootMargin: "50px" })
 *
 *   return <img ref={imgRef} src={isVisible ? src : placeholder} alt={alt} />
 * }
 * ```
 */
export const useIntersectionObserver = (
  ref: RefObject<Element>,
  options?: UseIntersectionObserverOptions,
): boolean => {
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    if (!ref.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsIntersecting(true)
        }
      },
      {
        root: options?.root ?? null,
        rootMargin: options?.rootMargin ?? "0px",
        threshold: options?.threshold ?? 0,
      },
    )

    observer.observe(ref.current)

    return () => observer.disconnect()
  }, [ref, options?.root, options?.rootMargin, options?.threshold])

  return isIntersecting
}
